import React from "react";
import { Shield } from "lucide-react";

import { Alert, Badge, Button, Callout, EmptyState, Modal, Table, cx, useConfirm } from "../../../Ui/index.jsx";

// One person's page access, as a side sheet. The shell opens it at once on the row's page grid; the long parts
// (hand-set exceptions) arrive from one person's detail, and the grid stays locked until they have.

// One clickable flag. Blue marks a grant added by hand, beyond what the department gives by default, so it can
// never be mistaken for a baseline right; a hand-set denial is red.
function FlagButton({ page, action, on, exception, onClick }) {
  const title = exception === true ? "Allowed by hand — click to deny"
    : exception === false ? "Denied by hand — click to follow their department again"
      : on ? "From their department — click to deny" : "Not granted — click to allow";
  const tone = exception === true ? "ds-text-info" : exception === false ? "ds-text-danger" : on ? "ds-text-success" : "ds-muted";
  return (
    <Button size="sm" variant="ghost" className={cx("ds-btn--icon", tone)} title={title} aria-label={`${page.label} ${action}: ${title}`} onClick={onClick}>
      {on ? "✓" : "–"}
    </Button>
  );
}

export function AccessDrawer({ user, matrix, onClose, act }) {
  const actions = matrix?.actions || ["create", "read", "update", "delete"];
  const pages = matrix?.pages || [];
  const grid = user.page_access || {};
  // Exceptions this person carries, so the grid can show what was set by hand.
  const exceptions = {};
  for (const row of user.page_overrides || []) exceptions[`${row.page}.${row.action}`] = row.allow;

  const confirm = useConfirm();
  const canReset = !user.is_superuser && !user.detailLoading && (user.page_overrides?.length > 0 || user.overrides?.length > 0);

  const columns = [
    { key: "label", header: "Page", render: (p) => (
      <span title={p.note || p.label} className={cx((grid[p.page] || []).length === 0 && "ds-muted")}>
        {p.label}
        {p.self_service && <> <Badge title="Their own records — everyone has this">own</Badge></>}
      </span>
    ) },
    ...actions.map((a) => ({
      key: a, header: a[0].toUpperCase(), align: "center",
      render: (p) => {
        if (!p.supports.includes(a)) return <span className="ds-muted" title={`${p.label} has no ${a} to grant`}>·</span>;
        const on = (grid[p.page] || []).includes(a);
        const exception = exceptions[`${p.page}.${a}`];
        const next = exception === undefined
          ? { action: "set_page_flag", page: p.page, page_action: a, allow: !on }
          : exception === true
            ? { action: "set_page_flag", page: p.page, page_action: a, allow: false }
            : { action: "clear_page_flag", page: p.page, page_action: a };
        return <FlagButton page={p} action={a} on={on} exception={exception} onClick={() => act(user.id, next)} />;
      },
    })),
  ];

  return (
    <Modal
      side
      title={user.name}
      subtitle={`${user.username} · ${user.department || "no dept"} · ${user.employment_type || "—"}`}
      actions={canReset && (
        <Button
          size="sm"
          title="Clear every hand-set page flag and legacy grant — back to exactly what their department gives them, as if freshly onboarded"
          onClick={() => confirm.ask({
            title: `Reset ${user.name} to their department's access?`,
            message: "This clears every permission set by hand for them. They get exactly what their department gives them.",
            confirmLabel: "Reset access",
            onConfirm: () => act(user.id, { action: "reset_page_overrides" }),
          })}
        >
          Reset to baseline
        </Button>
      )}
      onClose={onClose}
      closeOnBackdrop
    >
      {user.is_superuser ? (
        <Alert tone="warning"><Shield size={16} /> Superuser — full access, bypasses the model.</Alert>
      ) : user.detailLoading ? (
        <EmptyState>Loading access…</EmptyState>
      ) : user.detailError ? (
        <Alert tone="danger">{user.detailError}</Alert>
      ) : (
        <div className="Adm-Stack">
          <Callout soft>
            Tick a box to allow, click again to deny, click a third time to hand the
            decision back to their department. Read is the gate — clear it and the page
            disappears for them, and its API refuses them too. A <b className="ds-text-info">blue</b> tick
            was added by hand, beyond what their department gives everyone in it.
          </Callout>
          <Table flush columns={columns} rows={pages.map((p) => ({ ...p, id: p.page }))} />
        </div>
      )}
      {confirm.dialog}
    </Modal>
  );
}
